import { useState, useEffect, useRef, useMemo } from 'react';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import PhoneTopStrip from '../components/ui/PhoneTopStrip';
import LiveFeedbackRail from '../components/live/LiveFeedbackRail';
import useRenderMode from '../hooks/useRenderMode';
import { useZones, getSupervisorPrimaryZoneName } from '../hooks/useZones';
import { useAdmin } from '../contexts/AdminContext';
import { db, feedbackCollection } from '../firebase';
import * as XLSX from 'xlsx-js-style';
import {
  STYLE, statusStyle, buildSheet, downloadWorkbook, makeWorkbook,
  appendSheet, titleBlock, footerBlock, zebraStripe,
} from '../utils/excelReport';
import { isLegitimateFeedback } from '../utils/feedbackFilters';
import { resolveSupervisorIxoraIds, whereFeedbackByAnySupervisorId } from '../utils/supervisorIds';
// Import actual Firebase functions
import { query, getDocs, where, collection, doc, getDoc, documentId, getCountFromServer } from 'firebase/firestore';

import { useNavigate, Navigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  const mode = useRenderMode();
  const { isAdmin, isSiteManager, user } = useAdmin();
  // Zones come from Firestore (`sites/` where areaNumber is set). Replaces the
  // ladder of `if (areaNum === 1) return 'Charminar'` that used to live here.
  const { zones, zoneByAreaNumber, zoneById } = useZones();
  const supervisorZoneName = (s) => getSupervisorPrimaryZoneName(s, zoneById, zoneByAreaNumber);
  // 'Manager' is a legacy non-zone allotment sentinel; keep it in the picker.
  const areas = useMemo(
    () => ['All', 'Manager', ...zones.map((z) => z.name)],
    [zones],
  );

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const [timeRange, setTimeRange] = useState('30D');
  const [windowCount, setWindowCount] = useState(0);
  const [windowLoading, setWindowLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [areasLoading, setAreasLoading] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(true);
  const [feedbackValuesLoading, setFeedbackValuesLoading] = useState(true);
  const [monthDownloadLoading, setMonthDownloadLoading] = useState(false);
  const [avg30, setAvg30] = useState(null);
  const [responsePct, setResponsePct] = useState(null);
  const dateInputRef = useRef(null);
  
  // Format date as YYYY-MM-DD for input element
  const formattedDate = selectedDate.toISOString().split('T')[0];
  
  // Create stats with default values
  const [stats, setStats] = useState([
    { title: 'Total Feedback', value: '0', color: '#EA580C' },
    { title: 'Feedback Today', value: '0', color: '#EA580C' },
    { title: 'Feedback This Month', value: '0', color: '#EA580C' },
    { title: 'Feedback This Year', value: '0', color: '#EA580C' },
  ]);
  
  // Detailed stats start empty and fill in from `zones` once it loads.
  const [detailedStats, setDetailedStats] = useState([]);

  // Resolves a feedback doc's shop_area (string '1'..'7') to a zone name via
  // the live useZones() map. Falls back to 'Other' if the zone isn't loaded
  // yet or the area number doesn't map.
  const getAreaFromFeedback = (feedback) => {
    const areaNum = parseInt(feedback.shop_area, 10);
    const z = zoneByAreaNumber.get(areaNum);
    return z ? z.name : 'Other';
  };

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Update stats based on selected date
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setAreasLoading(true);

        const selectedMonth = selectedDate.getMonth();
        const selectedYear = selectedDate.getFullYear();
        const todayDateString = formatDateString(selectedDate);
        const startOfMonth = new Date(selectedYear, selectedMonth, 1);
        const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
        const monthStartStr = formatDateString(startOfMonth);
        const monthEndStr = formatDateString(endOfMonth);

        const formattedDayDate = selectedDate.toLocaleDateString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric',
        });
        const formattedMonthDate = selectedDate.toLocaleDateString('en-US', {
          month: 'long', year: 'numeric',
        });

        const [totalsSnap, todaySnap, monthSnap, yearSnap] = await Promise.all([
          getDoc(doc(db, 'feedbackStats', 'totals')),
          getDoc(doc(db, 'feedbackDaily', todayDateString)),
          getDocs(query(
            collection(db, 'feedbackDaily'),
            where(documentId(), '>=', monthStartStr),
            where(documentId(), '<=', monthEndStr),
          )),
          getDocs(query(
            collection(db, 'feedbackDaily'),
            where(documentId(), '>=', `${selectedYear}-01-01`),
            where(documentId(), '<=', `${selectedYear}-12-31`),
          )),
        ]);

        const totalCount = totalsSnap.data()?.totalAllTime ?? 0;
        const dayCount = totalsSnap.exists ? (todaySnap.data()?.count ?? 0) : 0;
        const todayAreaCounts = todaySnap.data()?.byArea ?? {};

        let monthCount = 0;
        const areaCounts = {};
        monthSnap.forEach((d) => {
          monthCount += d.data().count ?? 0;
          Object.entries(d.data().byArea ?? {}).forEach(([area, n]) => {
            areaCounts[area] = (areaCounts[area] ?? 0) + n;
          });
        });

        let yearCount = 0;
        yearSnap.forEach((d) => { yearCount += d.data().count ?? 0; });

        setStats([
          { title: 'Total Feedback',                      value: totalCount.toString(),  color: '#EA580C' },
          { title: `Feedback on ${formattedDayDate}`,     value: dayCount.toString(),    color: '#EA580C' },
          { title: `Feedback in ${formattedMonthDate}`,   value: monthCount.toString(),  color: '#EA580C' },
          { title: `Feedback in ${selectedYear}`,         value: yearCount.toString(),   color: '#EA580C' },
        ]);

        // Drive the per-zone breakdown from the live zones list so renaming a
        // zone or adding an 8th never needs a code change.
        setDetailedStats(zones.map((z) => ({
          title: z.name,
          value: `${todayAreaCounts[z.name] ?? 0}/${areaCounts[z.name] ?? 0}`,
          color: 'var(--c-brand-600)',
        })));

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
        setAreasLoading(false);
      }
    };

    fetchData();
    // Re-run when zones load so the per-zone breakdown picks up the live list.
  }, [selectedDate, zones.length]);

  // Window count driven by the 7D/30D/6M/1Y/ALL pills.
  useEffect(() => {
    const fetchWindow = async () => {
      setWindowLoading(true);
      try {
        if (timeRange === 'ALL') {
          const snap = await getDoc(doc(db, 'feedbackStats', 'totals'));
          setWindowCount(snap.data()?.totalAllTime ?? 0);
          return;
        }
        const daysBack = { '7D': 7, '30D': 30, '6M': 183, '1Y': 365 }[timeRange] ?? 30;
        const end = new Date(selectedDate);
        const start = new Date(selectedDate);
        start.setDate(start.getDate() - (daysBack - 1));
        const snap = await getDocs(query(
          collection(db, 'feedbackDaily'),
          where(documentId(), '>=', formatDateString(start)),
          where(documentId(), '<=', formatDateString(end)),
        ));
        let count = 0;
        snap.forEach((d) => { count += d.data().count ?? 0; });
        setWindowCount(count);
      } catch (err) {
        console.error('Window query failed:', err);
        setWindowCount(0);
      } finally {
        setWindowLoading(false);
      }
    };
    fetchWindow();
  }, [timeRange, selectedDate]);

  const windowLabel = {
    '7D': 'LAST 7 DAYS',
    '30D': 'LAST 30 DAYS',
    '6M': 'LAST 6 MONTHS',
    '1Y': 'LAST YEAR',
    'ALL': 'ALL TIME',
  }[timeRange] || 'LAST 30 DAYS';

  // 30-day avg rating + today's response% (supervisors reporting today) — folded
  // in from the retired Homescreen so /dashboard exposes the same KPIs.
  // supervisors query is admin-only to avoid Firestore permission errors for guests.
  useEffect(() => {
    let cancelled = false;
    const isAdminLocal = isAdmin;
    const run = async () => {
      try {
        const end = new Date(selectedDate);
        const start = new Date(selectedDate);
        start.setDate(start.getDate() - 29);
        const endStr = formatDateString(end);
        const startStr = formatDateString(start);
        const selStr = formatDateString(selectedDate);

        // Avg rating from aggregates (public-readable)
        const d30Snap = await getDocs(query(
          collection(db, 'feedbackDaily'),
          where(documentId(), '>=', startStr),
          where(documentId(), '<=', endStr),
        ));
        if (cancelled) return;
        let ratingSum = 0, ratingCount = 0;
        d30Snap.forEach((d) => {
          ratingSum += d.data().ratingSum ?? 0;
          ratingCount += d.data().ratingCount ?? 0;
        });
        setAvg30(ratingCount > 0 ? ratingSum / ratingCount : null);

        // Response% requires raw feedback_collection (admin-only)
        if (isAdminLocal) {
          const [daySnap, supSnap] = await Promise.all([
            getDocs(query(feedbackCollection, where('feedback_collection_Date', '==', selStr))),
            getDocs(collection(db, 'supervisors')),
          ]);
          if (cancelled) return;
          const reporting = new Set();
          daySnap.forEach((d) => {
            const uid = d.data().feedback_collector_ixora_userID;
            if (uid) reporting.add(uid);
          });
          const totalSup = supSnap.size;
          setResponsePct(totalSup > 0
            ? { num: reporting.size, denom: totalSup, pct: Math.round((reporting.size / totalSup) * 100) }
            : null);
        } else {
          setResponsePct(null);
        }
      } catch (err) {
        console.error('Avg30/response query failed:', err);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedDate, isAdmin]);

  // Fetch supervisors — admin only (collection is Firestore-restricted for non-admins)
  useEffect(() => {
    if (!isAdmin) {
      setSupervisors([]);
      setSupervisorsLoading(false);
      return;
    }
    const fetchSupervisors = async () => {
      setSupervisorsLoading(true);
      try {
        const supervisorsRef = collection(db, 'supervisors');
        const querySnapshot = await getDocs(supervisorsRef);
        
        // Get selected date formatted as YYYY-MM-DD (changed from today to selectedDate)
        const formattedSelectedDate = formatDateString(selectedDate);
        
        const supervisorsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          checklistStatus: null, // Will be populated with the checklist status
          feedbacksToday: 0,    // Will be populated with feedback count
          totalFeedbacks: 0     // Will be populated with total feedback count
        }));
        
        // Sort by ixora_user_id if available
        supervisorsList.sort((a, b) => {
          if (!a.ixora_user_id) return 1;
          if (!b.ixora_user_id) return -1;
          return a.ixora_user_id.localeCompare(b.ixora_user_id, undefined, { numeric: true });
        });
        
        // Determine checklist status by checking checklist subcollection for selected date
        await Promise.all(supervisorsList.map(async (supervisor) => {
          try {
            const checklistRef = collection(db, 'supervisors', supervisor.id, 'checklist');
            const checklistQuery = query(
              checklistRef,
              where('checklistDate', '==', formattedSelectedDate)
            );
            const checklistSnapshot = await getDocs(checklistQuery);
            if (checklistSnapshot.size > 0) {
              // If any doc marks week-off, prefer that label; otherwise Done
              let status = 'Done';
              checklistSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data && data.isWeekOff) {
                  status = 'Week Off';
                }
              });
              supervisor.checklistStatus = status;
            } else {
              supervisor.checklistStatus = 'Not Done';
            }
          } catch (err) {
            console.error(`Error checking checklist for supervisor ${supervisor.id}:`, err);
            supervisor.checklistStatus = 'Not Done';
          }
        }));
        
        setSupervisors(supervisorsList);

        // After setting supervisors, fetch just the feedback data
        fetchSupervisorFeedbacks(supervisorsList);
      } catch (error) {
        console.error("Error fetching supervisors:", error);
      } finally {
        setSupervisorsLoading(false);
      }
    };
    
    fetchSupervisors();
  }, [selectedDate, isAdmin]); // Re-fetch when date or admin status changes

  // Fetch feedback counts for each supervisor
  const fetchSupervisorFeedbacks = async (supervisorsList) => {
    try {
      setFeedbackValuesLoading(true);
      const selectedDateString = formatDateString(selectedDate);
      const updatedSupervisors = [...supervisorsList];
      
      // Process each supervisor
      for (let i = 0; i < updatedSupervisors.length; i++) {
        const supervisor = updatedSupervisors[i];

        // Union over current + legacy ixora_user_id values — supervisors
        // migrated by migrate-legacy-supervisors.mjs have historical feedback
        // stamped with their pre-migration id. See utils/supervisorIds.js.
        const idClause = whereFeedbackByAnySupervisorId(supervisor);
        if (idClause) {
          try {
            // Query for selected date's feedbacks using count method
            const selectedDateQuery = query(
              feedbackCollection,
              where('feedback_collection_Date', '==', selectedDateString),
              idClause,
            );

            const selectedDateCountSnapshot = await getCountFromServer(selectedDateQuery);
            supervisor.feedbacksToday = selectedDateCountSnapshot.data().count;

            // Query for total feedbacks using count method
            const totalQuery = query(feedbackCollection, idClause);

            const totalCountSnapshot = await getCountFromServer(totalQuery);
            supervisor.totalFeedbacks = totalCountSnapshot.data().count;
          } catch (error) {
            console.error(`Error fetching feedbacks for supervisor ${supervisor.ixora_user_id}:`, error);
          }
        }
      }
      
      // Update state with the new data
      setSupervisors(updatedSupervisors);
    } catch (error) {
      console.error("Error fetching supervisor details:", error);
    } finally {
      setFeedbackValuesLoading(false);
    }
  };

  const handleDateChange = (e) => {
    // Check if the input is empty or invalid
    if (!e.target.value) {
      // Default to current date if the input is cleared
      setSelectedDate(new Date());
      setAreasLoading(true);
      setFeedbackValuesLoading(true);
      return;
    }
    
    // Try to create a new date from the input value
    const newDate = new Date(e.target.value);
    
    // Validate the date - if invalid, use current date
    if (isNaN(newDate.getTime())) {
      console.warn("Invalid date selected, defaulting to current date");
      setSelectedDate(new Date());
    } else {
      setSelectedDate(newDate);
    }
    
    setAreasLoading(true);
    setFeedbackValuesLoading(true);
  };

  const handleDateButtonClick = () => {
    // Directly open the date picker by triggering a click on the hidden input
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const [showDownloadPopup, setShowDownloadPopup] = useState(false);


  const _handleDownloadDayReport = async () => {
    try {
      setLoading(true);
      
      // Format the selected date
      const todayDateString = formatDateString(selectedDate);
      
      // Query for day's feedbacks
      const dayQuery = query(
        feedbackCollection,
        where('feedback_collection_Date', '==', todayDateString)
      );
      const daySnapshot = await getDocs(dayQuery);
      const dayFeedbacks = daySnapshot.docs.map(doc => doc.data()).filter(isLegitimateFeedback);
      
      // Convert to CSV format
      const headers = ["Date", "Time", "Shop Name", "Area", "Rating", "Phone Number"];
      const csvRows = [];
      
      // Add headers
      csvRows.push(headers.join(','));
      
      // Add data rows
      dayFeedbacks.forEach(feedback => {
        // Safely extract time from DateTime field
        let timeString = '';
        if (feedback.feedback_collection_DateTime && typeof feedback.feedback_collection_DateTime === 'string') {
          const timeParts = feedback.feedback_collection_DateTime.split(' ');
          timeString = timeParts.length > 1 ? timeParts[1] : '';
        } else if (feedback.feedback_collection_DateTime && feedback.feedback_collection_DateTime instanceof Date) {
          timeString = feedback.feedback_collection_DateTime.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        } else if (feedback.feedback_collection_DateTime) {
          // Try to convert to string if it's a timestamp or other type
          timeString = String(feedback.feedback_collection_DateTime);
        }
        
        const rowData = [
          feedback.feedback_collection_Date || '',
          timeString,
          feedback.shop_name || '',
          getAreaFromFeedback(feedback),
          feedback.feedback_stars || '',
          feedback.phone_number || ''
        ];
        
        // Escape any commas in the data
        const escapedRowData = rowData.map(field => {
          if (field.includes(',')) {
            return `"${field}"`;
          }
          return field;
        });
        
        csvRows.push(escapedRowData.join(','));
      });
      
      // Create and download the CSV file
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const formattedDateForFilename = selectedDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).replace(/\//g, '-');
      
      link.href = url;
      link.download = `Ixora_Feedback_Day_Report_${formattedDateForFilename}.csv`;
      link.click();
      
    } catch (error) {
      console.error("Error downloading day report:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadMonthReport = async () => {
    console.log('Starting month report download...');
    
    // Check if XLSX library is available
    if (typeof XLSX === 'undefined') {
      console.error('XLSX library not found!');
      alert('Error: XLSX library not available. Please refresh the page and try again.');
      return;
    }
    
    try {
      setMonthDownloadLoading(true);
      
      // Get the selected month range
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const startOfMonth = new Date(selectedYear, selectedMonth, 1);
      const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);
      
      const monthStartString = formatDateString(startOfMonth);
      const monthEndString = formatDateString(endOfMonth);
      
      // Query for month's feedbacks
      console.log(`Querying feedback for month: ${monthStartString} to ${monthEndString}`);
      const monthQuery = query(
        feedbackCollection,
        where('feedback_collection_Date', '>=', monthStartString),
        where('feedback_collection_Date', '<=', monthEndString)
      );
      const monthSnapshot = await getDocs(monthQuery);
      const monthFeedbacks = monthSnapshot.docs.map(doc => doc.data()).filter(isLegitimateFeedback);
      console.log(`Found ${monthFeedbacks.length} feedback entries for the month`);
      
      // Log a sample feedback entry to understand the data structure
      if (monthFeedbacks.length > 0) {
        console.log('Sample feedback entry:', monthFeedbacks[0]);
        console.log('DateTime field type:', typeof monthFeedbacks[0].feedback_collection_DateTime);
        console.log('DateTime field value:', monthFeedbacks[0].feedback_collection_DateTime);
      }
      
      // Get all supervisors to check their checklist status for each date
      console.log('Fetching supervisors...');
      let supervisors = [];
      try {
        const supervisorsQuery = query(collection(db, 'supervisors'));
        const supervisorsSnapshot = await getDocs(supervisorsQuery);
        supervisors = supervisorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log(`Found ${supervisors.length} supervisors`);
      } catch (error) {
        console.error('Error fetching supervisors:', error);
        // If we can't get supervisors, create a basic report with just feedback
        console.log('Continuing with basic report (feedback only)');
      }
      
      // Create a map to store checklist status by date and supervisor
      const checklistMap = {};
      
      // For each supervisor, check their checklist status for each date in the month
      if (supervisors.length > 0) {
        console.log('Processing checklist data for supervisors...');
        
        // Initialize checklistMap with all dates and supervisors set to "Not Done"
        for (let day = 1; day <= endOfMonth.getDate(); day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);
          const dateString = formatDateString(currentDate);
          checklistMap[dateString] = {};
          
          supervisors.forEach(supervisor => {
            checklistMap[dateString][supervisor.id] = 'Not Done';
          });
        }
        
        // Process all supervisors in parallel for better performance
        await Promise.all(supervisors.map(async (supervisor) => {
          try {
            const checklistRef = collection(db, 'supervisors', supervisor.id, 'checklist');
            
            // Get all checklist documents for this supervisor for the entire month
            const checklistQuery = query(
              checklistRef,
              where('checklistDate', '>=', monthStartString),
              where('checklistDate', '<=', monthEndString)
            );
            
            const checklistSnapshot = await getDocs(checklistQuery);
            
            // Process each checklist document
            checklistSnapshot.forEach((docSnap) => {
              const data = docSnap.data();
              if (data && data.checklistDate) {
                let status = 'Done'; // Default to Done if document exists
                
                // Check for week-off status (same logic as date-wise report)
                if (data.isWeekOff) {
                  status = 'Week Off';
                }
                
                // Update the checklist map
                if (checklistMap[data.checklistDate]) {
                  checklistMap[data.checklistDate][supervisor.id] = status;
                }
              }
            });
            
          } catch (err) {
            console.error(`Error checking checklist for supervisor ${supervisor.id}:`, err);
            // Errors already handled by initialization - all dates default to "Not Done"
          }
        }));
        
        console.log('Checklist processing completed');
        
        // Debug: Log a sample of checklist data
        const sampleDate = formatDateString(new Date(selectedYear, selectedMonth, 1));
        console.log(`Sample checklist data for ${sampleDate}:`, checklistMap[sampleDate]);
      }
      
      // Create comprehensive report with both feedback and checklist data
      console.log('Creating Excel workbook...');

      const workbook = makeWorkbook();
      const monthName = selectedDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthNameLong = selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      const daysInMonth = endOfMonth.getDate();

      // ---------- Aggregate ----------
      // Sort supervisors by their ixora_user_id (same as date-wise report)
      supervisors.sort((a, b) => {
        if (!a.ixora_user_id) return 1;
        if (!b.ixora_user_id) return -1;
        return a.ixora_user_id.localeCompare(b.ixora_user_id, undefined, { numeric: true });
      });

      const supervisorStats = {};
      supervisors.forEach(supervisor => {
        supervisorStats[supervisor.id] = {
          name: supervisor.display_name || supervisor.name || supervisor.id,
          ixoraUserId: supervisor.ixora_user_id || '',
          allotment: supervisorZoneName(supervisor) || supervisor.ixora_allotment || '',
          totalChecklist: 0,
          totalFeedbacks: 0,
          dailyData: {},
        };
      });

      monthFeedbacks.forEach(feedback => {
        const date = feedback.feedback_collection_Date;
        const supervisorUserId = feedback.feedback_collector_ixora_userID;
        if (!supervisorUserId) return;
        // Match feedback to supervisor via current id OR any legacy id (post-
        // migrate-legacy-supervisors.mjs, historical feedback still references
        // the pre-migration ixora_user_id).
        const supervisor = supervisors.find(s =>
          s.ixora_user_id === supervisorUserId ||
          (Array.isArray(s.legacyIxoraUserIds) && s.legacyIxoraUserIds.includes(supervisorUserId))
        );
        if (!supervisor || !supervisorStats[supervisor.id]) return;
        supervisorStats[supervisor.id].totalFeedbacks++;
        if (!supervisorStats[supervisor.id].dailyData[date]) {
          supervisorStats[supervisor.id].dailyData[date] = { feedbacks: 0, checklist: 'Not Done' };
        }
        supervisorStats[supervisor.id].dailyData[date].feedbacks++;
      });

      supervisors.forEach(supervisor => {
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);
          const dateString = formatDateString(currentDate);
          const status = checklistMap[dateString]?.[supervisor.id] || 'Not Done';
          if (status === 'Done' || status === 'Week Off') {
            supervisorStats[supervisor.id].totalChecklist++;
          }
          if (!supervisorStats[supervisor.id].dailyData[dateString]) {
            supervisorStats[supervisor.id].dailyData[dateString] = { feedbacks: 0, checklist: status };
          } else {
            supervisorStats[supervisor.id].dailyData[dateString].checklist = status;
          }
        }
      });

      // ---------- Sheet 1: SUMMARY ----------
      const SUMMARY_COLS = 6;
      const possibleChecklists = supervisors.length * daysInMonth;
      const totalChecklistDone = supervisors.reduce((sum, s) => sum + supervisorStats[s.id].totalChecklist, 0);
      const totalFeedbacks = supervisors.reduce((sum, s) => sum + supervisorStats[s.id].totalFeedbacks, 0);
      const completionPct = possibleChecklists > 0 ? totalChecklistDone / possibleChecklists : 0;

      const topPerformers = [...supervisors]
        .sort((a, b) => supervisorStats[b.id].totalFeedbacks - supervisorStats[a.id].totalFeedbacks)
        .slice(0, 10);

      const sumTb = titleBlock({
        title: `IXORA · MONTHLY OPERATIONS · ${monthNameLong}`,
        subtitle: `${supervisors.length} SUPERVISORS  ·  ${daysInMonth} DAYS  ·  ${totalFeedbacks.toLocaleString('en-IN')} FEEDBACKS COLLECTED`,
        spanCols: SUMMARY_COLS,
      });

      const summaryAoa = [
        ...sumTb.rows,
        [
          { v: 'METRIC', s: STYLE.headerPrimary },
          { v: 'VALUE', s: STYLE.headerPrimary },
          { v: '', s: STYLE.headerPrimary },
          { v: 'METRIC', s: STYLE.headerPrimary },
          { v: 'VALUE', s: STYLE.headerPrimary },
          { v: '', s: STYLE.headerPrimary },
        ],
        [
          { v: 'Active supervisors', s: STYLE.bodyText },
          { v: supervisors.length, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          '',
          { v: 'Possible checklists', s: STYLE.bodyText },
          { v: possibleChecklists, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          '',
        ],
        [
          { v: 'Days in month', s: STYLE.bodyText },
          { v: daysInMonth, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          '',
          { v: 'Completed checklists', s: STYLE.bodyText },
          { v: totalChecklistDone, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          '',
        ],
        [
          { v: 'Total feedbacks', s: STYLE.bodyText },
          { v: totalFeedbacks, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          '',
          { v: 'Completion rate', s: STYLE.bodyText },
          { v: completionPct, t: 'n', s: STYLE.bodyNum, z: '0.0%' },
          '',
        ],
        [
          { v: 'Avg feedbacks / supervisor', s: STYLE.bodyText },
          { v: supervisors.length > 0 ? totalFeedbacks / supervisors.length : 0, t: 'n', s: STYLE.bodyNum, z: '0.0' },
          '',
          { v: 'Avg feedbacks / day', s: STYLE.bodyText },
          { v: daysInMonth > 0 ? totalFeedbacks / daysInMonth : 0, t: 'n', s: STYLE.bodyNum, z: '0.0' },
          '',
        ],
        Array(SUMMARY_COLS).fill(''),
        [
          { v: `TOP 10 BY FEEDBACKS · ${monthName}`, s: STYLE.subtitle },
          { v: '', s: STYLE.subtitle }, { v: '', s: STYLE.subtitle },
          { v: '', s: STYLE.subtitle }, { v: '', s: STYLE.subtitle }, { v: '', s: STYLE.subtitle },
        ],
        [
          { v: 'Rank', s: STYLE.headerPrimary },
          { v: 'Supervisor', s: STYLE.headerPrimary },
          { v: 'ID', s: STYLE.headerPrimary },
          { v: 'Allotment', s: STYLE.headerPrimary },
          { v: 'Checklists', s: STYLE.headerPrimary },
          { v: 'Feedbacks', s: STYLE.headerPrimary },
        ],
      ];

      topPerformers.forEach((s, idx) => {
        const stats = supervisorStats[s.id];
        summaryAoa.push([
          { v: idx + 1, t: 'n', s: STYLE.bodyId },
          { v: stats.name, s: STYLE.bodyText },
          { v: stats.ixoraUserId || '—', s: STYLE.bodyId },
          { v: stats.allotment || '—', s: STYLE.bodyTextCenter },
          { v: stats.totalChecklist, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          { v: stats.totalFeedbacks, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
        ]);
      });

      const sumFooter = footerBlock({
        generatedBy: user?.email || 'dashboard user',
        contextLine: `Month: ${monthNameLong}  ·  Source: feedback_collection + supervisors/{id}/checklist`,
        spanCols: SUMMARY_COLS,
      });
      const sumFooterBase = summaryAoa.length;
      summaryAoa.push(...sumFooter.rows);

      // Zebra-stripe the top-10 table (after the merged subtitle + header).
      const topTableStart = 11; // 3 title + 1 header + 4 metric rows + 1 spacer + 1 subtitle + 1 top-10 header = 11
      const topTableEnd = topTableStart + topPerformers.length - 1;
      zebraStripe(summaryAoa.slice(0, topTableEnd + 1), topTableStart);

      const summaryWs = buildSheet({
        aoa: summaryAoa,
        cols: [{ wch: 26 }, { wch: 16 }, { wch: 4 }, { wch: 26 }, { wch: 16 }, { wch: 4 }],
        merges: [
          ...sumTb.merges,
          // Subtitle band before top-10 (row 9, all cols).
          { s: { r: 9, c: 0 }, e: { r: 9, c: SUMMARY_COLS - 1 } },
          ...sumFooter.merges(sumFooterBase),
        ],
        freeze: { ySplit: 3 },
      });
      summaryWs['!rows'] = [{ hpt: 28 }, { hpt: 18 }, { hpt: 6 }];
      appendSheet(workbook, summaryWs, 'Summary');

      // ---------- Sheet 2: DAILY MATRIX ----------
      const FIXED_COLS = 6;
      const MATRIX_COLS = FIXED_COLS + daysInMonth * 2;

      const matrixTb = titleBlock({
        title: `IXORA · DAILY MATRIX · ${monthNameLong}`,
        subtitle: `Each day shows  CL (checklist status)  /  FB (feedbacks collected)`,
        spanCols: MATRIX_COLS,
      });

      // Header row: 6 fixed labels + per-day merged labels (1st May, 2nd May…).
      const headerRow = [
        { v: 'Sr', s: STYLE.headerPrimary },
        { v: 'Supervisor', s: STYLE.headerPrimary },
        { v: 'ID', s: STYLE.headerPrimary },
        { v: 'Allotment', s: STYLE.headerPrimary },
        { v: `CL · ${monthName}`, s: STYLE.headerPrimary },
        { v: `FB · ${monthName}`, s: STYLE.headerPrimary },
      ];
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(selectedYear, selectedMonth, day);
        const dow = currentDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
        const monthStr = currentDate.toLocaleDateString('en-US', { month: 'short' });
        headerRow.push({ v: `${day}${getOrdinalSuffix(day)} ${monthStr}\n${dow}`, s: STYLE.headerPrimary });
        headerRow.push({ v: '', s: STYLE.headerPrimary });
      }

      // Sub-header row: 6 blanks + CL/FB pairs.
      const subHeaderRow = [];
      for (let i = 0; i < FIXED_COLS; i++) subHeaderRow.push({ v: '', s: STYLE.headerSecondary });
      for (let day = 1; day <= daysInMonth; day++) {
        subHeaderRow.push({ v: 'CL', s: STYLE.headerSecondary });
        subHeaderRow.push({ v: 'FB', s: STYLE.headerSecondary });
      }

      const matrixAoa = [...matrixTb.rows, headerRow, subHeaderRow];

      // Data rows.
      supervisors.forEach((supervisor, index) => {
        const stats = supervisorStats[supervisor.id];
        const row = [
          { v: index + 1, t: 'n', s: STYLE.bodyId },
          { v: stats.name, s: STYLE.bodyText },
          { v: stats.ixoraUserId || '—', s: STYLE.bodyId },
          { v: stats.allotment || '—', s: STYLE.bodyTextCenter },
          { v: stats.totalChecklist, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
          { v: stats.totalFeedbacks, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
        ];
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);
          const dateString = formatDateString(currentDate);
          const dailyData = stats.dailyData[dateString] || { feedbacks: 0, checklist: 'Not Done' };
          const clShort = dailyData.checklist === 'Done' ? '✓'
            : dailyData.checklist === 'Week Off' ? 'WO'
            : '✗';
          row.push({ v: clShort, s: statusStyle(dailyData.checklist) });
          const fbVal = dailyData.feedbacks || 0;
          row.push({
            v: fbVal,
            t: 'n',
            z: '#,##0',
            s: fbVal > 0
              ? { ...STYLE.bodyNum, font: { ...STYLE.bodyNum.font, bold: true, color: { rgb: '047857' } } }
              : { ...STYLE.bodyNum, font: { ...STYLE.bodyNum.font, color: { rgb: '9CA3AF' } } },
          });
        }
        matrixAoa.push(row);
      });

      // Column totals row at the bottom.
      const dayTotals = Array.from({ length: daysInMonth }, () => ({ done: 0, notDone: 0, weekOff: 0, fb: 0 }));
      supervisors.forEach(supervisor => {
        const stats = supervisorStats[supervisor.id];
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);
          const dateString = formatDateString(currentDate);
          const dailyData = stats.dailyData[dateString] || { feedbacks: 0, checklist: 'Not Done' };
          if (dailyData.checklist === 'Done') dayTotals[day - 1].done++;
          else if (dailyData.checklist === 'Week Off') dayTotals[day - 1].weekOff++;
          else dayTotals[day - 1].notDone++;
          dayTotals[day - 1].fb += dailyData.feedbacks || 0;
        }
      });

      const totalsRow = [
        { v: 'TOTAL', s: STYLE.totalsRow },
        { v: `${supervisors.length} supervisors`, s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: totalChecklistDone, t: 'n', s: STYLE.totalsRow, z: '#,##0' },
        { v: totalFeedbacks, t: 'n', s: STYLE.totalsRow, z: '#,##0' },
      ];
      for (let day = 1; day <= daysInMonth; day++) {
        const t = dayTotals[day - 1];
        totalsRow.push({ v: t.done, t: 'n', s: STYLE.totalsRow, z: '#,##0' });
        totalsRow.push({ v: t.fb, t: 'n', s: STYLE.totalsRow, z: '#,##0' });
      }
      matrixAoa.push(totalsRow);

      // Footer.
      const matrixFooter = footerBlock({
        generatedBy: user?.email || 'dashboard user',
        contextLine: `Month: ${monthNameLong}  ·  CL = checklist status  ·  FB = feedbacks collected  ·  ✓ done · ✗ not done · WO week off`,
        spanCols: MATRIX_COLS,
      });
      const matrixFooterBase = matrixAoa.length;
      matrixAoa.push(...matrixFooter.rows);

      // Merges: each fixed column spans rows 4-5; each date label spans 2 cols on row 4.
      const matrixMerges = [
        ...matrixTb.merges,
        // Fixed col header merges (rows 3-4).
        ...Array.from({ length: FIXED_COLS }, (_, c) => ({
          s: { r: 3, c }, e: { r: 4, c },
        })),
        // Per-day header merges (row 3, 2 cols each).
        ...Array.from({ length: daysInMonth }, (_, i) => ({
          s: { r: 3, c: FIXED_COLS + i * 2 },
          e: { r: 3, c: FIXED_COLS + i * 2 + 1 },
        })),
        ...matrixFooter.merges(matrixFooterBase),
      ];

      // Zebra-stripe the supervisor rows (between sub-header and totals).
      const dataStart = 5;
      const dataEnd = dataStart + supervisors.length - 1;
      zebraStripe(matrixAoa.slice(0, dataEnd + 1), dataStart);

      const headerRef = XLSX.utils.encode_cell({ r: 3, c: 0 });
      const lastDataRef = XLSX.utils.encode_cell({ r: dataEnd, c: MATRIX_COLS - 1 });

      const matrixCols = [
        { wch: 5 }, { wch: 26 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 12 },
      ];
      for (let day = 1; day <= daysInMonth; day++) {
        matrixCols.push({ wch: 5 }, { wch: 6 });
      }

      const matrixWs = buildSheet({
        aoa: matrixAoa,
        cols: matrixCols,
        merges: matrixMerges,
        freeze: { xSplit: FIXED_COLS, ySplit: 5 },
        autoFilter: `${headerRef}:${lastDataRef}`,
      });
      matrixWs['!rows'] = [
        { hpt: 28 }, { hpt: 18 }, { hpt: 6 }, { hpt: 32 }, { hpt: 18 },
      ];
      appendSheet(workbook, matrixWs, `Matrix ${monthName}`);

      const formattedMonthYearForFilename = selectedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
      }).replace(/\//g, '-');

      downloadWorkbook(workbook, `Ixora_MonthlyReport_${formattedMonthYearForFilename}.xlsx`);
      console.log('Month report download completed successfully!');
      
    } catch (error) {
      console.error("Error downloading month report:", error);
      
      // Show user-friendly error message
      let errorMessage = "An error occurred while generating the report.";
      if (error.message.includes('XLSX')) {
        errorMessage = "Excel generation library error. Please refresh the page and try again.";
      } else if (error.message.includes('db is not defined')) {
        errorMessage = "Database connection error. Please refresh the page and try again.";
      } else if (error.message.includes('permission')) {
        errorMessage = "Permission denied. Please check your access rights.";
      }
      
      alert(`Download Failed: ${errorMessage}\n\nTechnical details: ${error.message}`);
    } finally {
      setMonthDownloadLoading(false);
      console.log('Month report download process finished');
    }
  };

  // Helper function to get the ordinal suffix for the day (1st, 2nd, 3rd, etc)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const _renderDetailedStats = () => {
    // Per DESIGN.md: drop the 7-colour area palette. Amber chip marks the leader
    // (highest today count); everyone else is neutral. Ties favour the first one.
    const parseToday = (v) => parseInt((v || '0').toString().split('/')[0], 10) || 0;
    const maxToday = detailedStats.reduce((m, s) => Math.max(m, parseToday(s.value)), 0);
    const leaderIdx = maxToday > 0
      ? detailedStats.findIndex((s) => parseToday(s.value) === maxToday)
      : -1;
    return (
      <section style={{ marginBottom: 'var(--sp-5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <h2 className="section-title" style={{ fontSize: 'var(--fs-md)' }}>Today / Month by Area</h2>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--c-ink-4)' }}>Today · Month</span>
        </div>
        <div className="grid-areas">
          {detailedStats.map((stat, index) => {
            const [todayCount, monthCount] = stat.value.split('/');
            const isLeader = index === leaderIdx;
            const accent = isLeader ? 'var(--c-brand-600)' : 'var(--c-line-2)';
            return (
              <div key={index} className="stat" style={{ ['--accent']: accent }}>
                <div className="stat-accent" />
                <div className="stat-title" title={stat.title} style={{
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{stat.title}</div>
                {areasLoading ? (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="skeleton" style={{ height: 22, width: '70%' }} />
                  </div>
                ) : (
                  <div className="nums" style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 6 }}>
                    <span style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, color: isLeader ? 'var(--c-brand-700)' : 'var(--c-ink)' }}>
                      {todayCount}
                    </span>
                    <span style={{ color: 'var(--c-ink-4)' }}>/</span>
                    <span style={{ fontSize: 'var(--fs-md)', color: 'var(--c-ink-3)', fontWeight: 500 }}>
                      {monthCount}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Add a new state for selected area
  const [selectedArea, setSelectedArea] = useState("All");

  // Resolves an area name (with old aliases) to its zone areaNumber via the
  // live zones list. Returns null for non-zone selections like 'All'/'Manager'.
  const _getAreaNumber = (areaName) => {
    const ALIASES = {
      'Police Station': 'Police Stations - Hyderabad',
      'Police Station Hyderabad': 'Police Stations - Hyderabad',
      'Police Station Cyberabad': 'Police Stations - Cyberabad',
    };
    const canonical = ALIASES[areaName] ?? areaName;
    const z = zones.find((zone) => zone.name === canonical);
    return z ? z.areaNumber : null;
  };

  // Filters supervisors by the selected area. Reads the dual shape: prefers
  // the new `allotted_site_ids` array, falls back to legacy `ixora_allotment`.
  // 'Manager' stays a sentinel that only matches the literal legacy value.
  const getFilteredSupervisors = () => {
    if (selectedArea === 'All') return supervisors;
    if (selectedArea === 'Manager') {
      return supervisors.filter(
        (s) => String(s.ixora_allotment || '').trim().toLowerCase() === 'manager',
      );
    }
    return supervisors.filter((s) => supervisorZoneName(s) === selectedArea);
  };

  // Get the filtered supervisors when needed
  const filteredSupervisors = getFilteredSupervisors();

  // Reset loading state when area changes
  useEffect(() => {
    if (supervisors.length > 0) {
      setFeedbackValuesLoading(true);
      // Immediate timeout to allow the state update to be processed
      setTimeout(() => setFeedbackValuesLoading(false), 100);
    }
  }, [selectedArea]);

  // Site managers who are not admins have no business on the admin dashboard.
  // Redirect them immediately to their scoped view.
  // IMPORTANT: this must come AFTER all hooks to satisfy Rules of Hooks.
  if (!isAdmin && isSiteManager) return <Navigate to="/site-view" replace />;

  // Download the currently visible supervisor table as a branded Excel file:
  // amber title bar, mono headers, status colour-coding, totals row, frozen
  // header + name column, autofilter, and a metadata footer.
  const handleDownloadSupervisorTable = () => {
    try {
      const supervisorsToExport = getFilteredSupervisors();
      const tableDateLong = selectedDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
      const filenameDate = selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
      const areaTag = selectedArea && selectedArea !== 'All' ? selectedArea.replace(/\s+/g, '_') : 'AllAreas';

      const headerRow = [
        { v: 'Sr', s: STYLE.headerPrimary },
        { v: 'Supervisor', s: STYLE.headerPrimary },
        { v: 'ID', s: STYLE.headerPrimary },
        { v: 'Allotment', s: STYLE.headerPrimary },
        { v: 'Checklist', s: STYLE.headerPrimary },
        { v: 'Feedbacks', s: STYLE.headerPrimary },
      ];

      // Title block (3 rows) + header (1 row) = data starts at row 4.
      const COLS = 6;
      const tb = titleBlock({
        title: `IXORA · SUPERVISOR DAY REPORT`,
        subtitle: `${tableDateLong}  ·  AREA: ${selectedArea || 'All'}  ·  ${supervisorsToExport.length} SUPERVISORS`,
        spanCols: COLS,
      });

      const aoa = [...tb.rows, headerRow];

      // Body rows.
      let doneCount = 0, notDoneCount = 0, weekOffCount = 0, feedbackTotal = 0;
      supervisorsToExport.forEach((s, idx) => {
        const status = s.checklistStatus || '—';
        const fb = Number(s.feedbacksToday ?? 0) || 0;
        if (status === 'Done') doneCount++;
        else if (status === 'Not Done') notDoneCount++;
        else if (status === 'Week Off') weekOffCount++;
        feedbackTotal += fb;

        aoa.push([
          { v: idx + 1, t: 'n', s: STYLE.bodyId },
          { v: s.display_name || '—', s: STYLE.bodyText },
          { v: s.ixora_user_id || '—', s: STYLE.bodyId },
          { v: supervisorZoneName(s) || s.ixora_allotment || '—', s: STYLE.bodyTextCenter },
          { v: status, s: statusStyle(status) },
          { v: fb, t: 'n', s: STYLE.bodyNum, z: '#,##0' },
        ]);
      });

      // Totals row.
      aoa.push([
        { v: 'TOTAL', s: STYLE.totalsRow },
        { v: `${supervisorsToExport.length} supervisors`, s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: `${doneCount} done · ${notDoneCount} not · ${weekOffCount} off`, s: STYLE.totalsRow },
        { v: feedbackTotal, t: 'n', s: STYLE.totalsRow, z: '#,##0' },
      ]);

      // Footer.
      const fb = footerBlock({
        generatedBy: user?.email || 'dashboard user',
        contextLine: `Filter: area="${selectedArea || 'All'}"  ·  Date: ${tableDateLong}`,
        spanCols: COLS,
      });
      const footerBase = aoa.length;
      aoa.push(...fb.rows);

      // Apply zebra striping to body rows (between header and totals).
      const dataStart = 4;
      const dataEnd = dataStart + supervisorsToExport.length - 1;
      zebraStripe(aoa.slice(0, dataEnd + 1), dataStart);

      const headerRef = XLSX.utils.encode_cell({ r: 3, c: 0 });
      const lastDataRef = XLSX.utils.encode_cell({ r: dataEnd, c: COLS - 1 });

      const ws = buildSheet({
        aoa,
        cols: [{ wch: 5 }, { wch: 28 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 12 }],
        merges: [
          ...tb.merges,
          ...fb.merges(footerBase),
        ],
        freeze: { xSplit: 2, ySplit: 4 },
        autoFilter: `${headerRef}:${lastDataRef}`,
      });

      // Row heights for the title block + header.
      ws['!rows'] = [
        { hpt: 28 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 },
      ];

      const wb = makeWorkbook();
      appendSheet(wb, ws, `Day ${selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`);
      downloadWorkbook(wb, `Ixora_DayReport_${areaTag}_${filenameDate}.xlsx`);
    } catch (e) {
      console.error('Error exporting supervisor table:', e);
      alert('Export failed. Please try again.');
    }
  };

  // -----------------------------------------------------------------------
  // Render — Departure Board layout (2026-05-17). Desk + wall use the new
  // top-strip + poster + area board + persistent live rail. Phone falls
  // back to the legacy AppShell wrap with a stripped-down view.
  // -----------------------------------------------------------------------

  // Helpers used by the new render:
  const parseToday = (v) => parseInt((v || '0').toString().split('/')[0], 10) || 0;
  const parseMonth = (v) => parseInt((v || '0').toString().split('/')[1] || '0', 10) || 0;

  // Derive a sorted area list for the departure board with rank + chip type.
  // Sort by today's count desc (Dashboard hero = today's count). Tie-break by month.
  const areaChip = (title) => {
    // 6 = Police Stations Hyderabad, 7 = Police Stations Cyberabad
    if (title === 'Police Station' || title === 'Police Station Cyberabad') return 'ps';
    if (title === 'Karwan') return 'shop'; // SHOPS per spec; can become MIX later
    return 'shop';
  };
  const areaLabel = (title) => {
    if (title === 'Police Station') return { name: 'Police Stations', tag: 'HYD' };
    if (title === 'Police Station Cyberabad') return { name: 'Police Stations', tag: 'CYB' };
    return { name: title, tag: null };
  };

  const boardRows = (() => {
    const totalToday = detailedStats.reduce((acc, s) => acc + parseToday(s.value), 0);
    const totalMonth = detailedStats.reduce((acc, s) => acc + parseMonth(s.value), 0);
    const enriched = detailedStats.map((s) => {
      const today = parseToday(s.value);
      const month = parseMonth(s.value);
      return {
        title: s.title,
        today,
        month,
        share: totalMonth > 0 ? (month / totalMonth) * 100 : 0,
        chip: areaChip(s.title),
        label: areaLabel(s.title),
      };
    });
    enriched.sort((a, b) => (b.today - a.today) || (b.month - a.month));
    return { rows: enriched, totalToday, totalMonth };
  })();

  // Today's hero count + month count for the poster
  const todayCount = parseInt((stats[1]?.value || '0'), 10) || 0;
  const _monthCount = parseInt((stats[2]?.value || '0'), 10) || 0;
  const _yearCount  = parseInt((stats[3]?.value || '0'), 10) || 0;
  const totalCount = parseInt((stats[0]?.value || '0'), 10) || 0;
  const leaderRow = boardRows.rows.find((r) => r.today > 0) || null;

  const timeStr = now.toLocaleTimeString('en-GB', { hour12: false });
  const _dateStr = now.toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();

  // Phone mode: Departure Board phone variant ----------------------------
  if (mode === 'phone') {
    return (
      <div className="db-page-phone">
        <input
          ref={dateInputRef}
          type="date"
          value={formattedDate}
          onChange={handleDateChange}
          className="sr-only"
          aria-label="Select date"
        />
        <PhoneTopStrip />

        <div className="db-ribbon-phone">
          <div className="title">OPERATIONS DASHBOARD · <em>HYDERABAD</em></div>
          <div className="pills">
            {['7D','30D','6M','1Y','ALL'].map((r) => (
              <button
                key={r}
                type="button"
                className={`db-pill${timeRange === r ? ' on' : ''}`}
                onClick={() => setTimeRange(r)}
              >{r}</button>
            ))}
            <button type="button" className="db-pill db-pill-date" onClick={handleDateButtonClick}>
              DATE · {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase()}
            </button>
            <button
              type="button"
              className="db-pill primary"
              onClick={() => setShowDownloadPopup((v) => !v)}
            >
              REPORTS ↓
            </button>
          </div>
          {showDownloadPopup && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              <button
                type="button"
                className="db-pill block"
                onClick={() => { setShowDownloadPopup(false); handleDownloadSupervisorTable(); }}
              >
                DAY REPORT · {(() => { const d = selectedDate.getDate(); return `${d}${getOrdinalSuffix(d)} ${selectedDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}`; })()}
              </button>
              <button
                type="button"
                className="db-pill primary block"
                onClick={() => { setShowDownloadPopup(false); handleDownloadMonthReport(); }}
                disabled={monthDownloadLoading}
              >
                {monthDownloadLoading ? 'GENERATING…' : `MONTH REPORT · ${selectedDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}`}
              </button>
            </div>
          )}
        </div>

        <div className="db-poster-phone">
          <div className="head">
            <div className="eye">FEEDBACK TODAY · <em>LIVE</em></div>
            <div className="stamp">{timeStr}</div>
          </div>
          <div className="hero">{loading ? '—' : todayCount.toLocaleString('en-IN')}</div>
          <div className="hero-label">
            feedback collected across <em>7 areas</em>.
            {leaderRow ? (
              <>
                {' '}<strong>{leaderRow.label.name}{leaderRow.label.tag ? ` (${leaderRow.label.tag})` : ''}</strong> leads with {leaderRow.today}.
              </>
            ) : null}
          </div>
        </div>

        <div className="db-stats-phone">
          <div>
            <div className="eb">WINDOW · <em>{timeRange}</em></div>
            <div className="v">{windowLoading ? '—' : windowCount.toLocaleString('en-IN')}</div>
            <div className="meta">{windowLabel}</div>
          </div>
          <div>
            <div className="eb">TOTAL · ALL TIME</div>
            <div className="v">{loading ? '—' : totalCount.toLocaleString('en-IN')}</div>
            <div className="meta">SINCE FIRST RECORD</div>
          </div>
          <div>
            <div className="eb">AVG · 30D</div>
            <div className="v">{avg30 != null ? avg30.toFixed(1) : '—'}<span className="unit">/5</span></div>
            <div className="meta">LAST 30 DAYS</div>
          </div>
          <div>
            <div className="eb">RESPONSE · TODAY</div>
            <div className="v">{responsePct?.pct != null ? responsePct.pct : '—'}<span className="unit">%</span></div>
            <div className="meta">{responsePct?.denom
              ? `${responsePct.num}/${responsePct.denom} SUP.`
              : '—'}</div>
          </div>
        </div>

        <div className="db-board-phone">
          {boardRows.rows.map((row, idx) => {
            const isLeader = idx === 0 && row.today > 0;
            return (
              <div key={row.title} className={`row${isLeader ? ' leader' : ''}`}>
                <div className="line1">
                  <span className="rank">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="nm">
                    {row.label.name}{row.label.tag ? ` · ${row.label.tag}` : ''}
                  </span>
                </div>
                <div className="line2">
                  <span className="accent">{row.today > 0 ? `+${row.today}` : '0'} TODAY</span>
                  {' · '}{row.month.toLocaleString('en-IN')} MTD
                  {boardRows.totalMonth > 0 ? ` · ${row.share.toFixed(1)}%` : ''}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--c-line-hard)', background: 'var(--c-bg-2)' }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.18em',
            textTransform: 'uppercase', color: 'var(--c-ink-2)', fontWeight: 700,
          }}>
            SUPERVISOR LIST · {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase()}
          </div>
          <div className="db-form-row" style={{ marginTop: 10, marginBottom: 0 }}>
            <label htmlFor="areaSelectorPhone">FILTER · AREA</label>
            <select
              id="areaSelectorPhone"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              {areas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        <div className="db-list-phone">
          {supervisorsLoading ? (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: 10, padding: '2rem', color: 'var(--c-ink-3)',
            }}>
              <div className="loading-spinner loading-spinner-large" />
              <span className="loading-text">Loading supervisors…</span>
            </div>
          ) : filteredSupervisors.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-ink-3)' }}>
              No supervisors for the selected area.
            </div>
          ) : (
            filteredSupervisors.map((supervisor) => {
              const status = supervisor.checklistStatus;
              const statusEl = status === 'Done'
                ? <span className="status-done" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--c-success)' }}>DONE</span>
                : status === 'Not Done'
                  ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--c-danger)' }}>NOT DONE</span>
                  : status === 'Week Off'
                    ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 600, color: 'var(--c-ink-3)' }}>WEEK OFF</span>
                    : <div className="spinner" style={{ margin: 0, width: 14, height: 14 }} />;
              return (
                <button
                  key={supervisor.id}
                  type="button"
                  className="item"
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '14px 16px', borderBottom: '1px solid var(--c-line-soft)', display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer' }}
                  onClick={() => navigate('/dashboard/supervisor-information', { state: { supervisorId: supervisor.id } })}
                >
                  <div className="row1">
                    <div className="nm" style={{ color: supervisor.userStatus === 'inactive' ? 'var(--c-ink-4)' : 'var(--c-ink)' }}>
                      {supervisor.display_name || '—'}{supervisor.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}
                    </div>
                    {statusEl}
                  </div>
                  <div className="stats-row">
                    <div>
                      <span className="l">ID</span>
                      <span className="v">{supervisor.ixora_user_id || '—'}</span>
                    </div>
                    <div>
                      <span className="l">ALLOTMENT</span>
                      <span className="v" style={{ fontFamily: "'Geist', sans-serif", fontSize: 12 }}>{supervisorZoneName(supervisor) || supervisor.ixora_allotment || '—'}</span>
                    </div>
                    <div>
                      <span className="l">TODAY / TOTAL</span>
                      <span className="v">
                        {feedbackValuesLoading
                          ? <span style={{ color: 'var(--c-ink-4)' }}>…</span>
                          : `${supervisor.feedbacksToday ?? 0} / ${supervisor.totalFeedbacks ?? 0}`}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {isAdmin && (
          <LiveFeedbackRail
            size={6}
            totalToday={todayCount}
            variant="phone"
            selectedDate={selectedDate}
          />
        )}
      </div>
    );
  }

  // ---------------------- Desk / wall: Departure Board ----------------------

  return (
    <div className="db-page">
      <input
        ref={dateInputRef}
        type="date"
        value={formattedDate}
        onChange={handleDateChange}
        className="sr-only"
        aria-label="Select date"
      />

      <DepartureTopStrip />

      {/* Ribbon: ops label + time-range + report actions */}
      <div className="db-ribbon">
        <div className="db-ribbon-title">
          <div className="db-ribbon-title-row">OPERATIONS DASHBOARD · <em>HYDERABAD</em></div>
          <div className="db-ribbon-caption">
            WINDOW <em>{timeRange}</em> · REPORTS FOR <em>{selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}</em>
          </div>
        </div>
        <div className="db-ribbon-actions">
          {['7D','30D','6M','1Y','ALL'].map((r) => (
            <button
              key={r}
              type="button"
              className={`db-pill${timeRange === r ? ' on' : ''}`}
              onClick={() => setTimeRange(r)}
            >{r}</button>
          ))}
          <button type="button" className="db-pill db-pill-date" onClick={handleDateButtonClick}>
            DATE · {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
          </button>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="db-pill primary"
              onClick={() => setShowDownloadPopup(!showDownloadPopup)}
              aria-expanded={showDownloadPopup}
              aria-haspopup="menu"
            >
              REPORTS ↓
            </button>
            {showDownloadPopup && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  background: 'var(--c-bg)',
                  border: '1px solid var(--c-line-hard)',
                  padding: 8,
                  zIndex: 50,
                  minWidth: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="db-pill"
                  style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  onClick={() => { setShowDownloadPopup(false); handleDownloadSupervisorTable(); }}
                >
                  DAY REPORT · {(() => { const d = selectedDate.getDate(); return `${d}${getOrdinalSuffix(d)} ${selectedDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}`; })()}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="db-pill primary"
                  style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  onClick={() => { setShowDownloadPopup(false); handleDownloadMonthReport(); }}
                  disabled={monthDownloadLoading}
                >
                  {monthDownloadLoading ? 'GENERATING…' : `MONTH REPORT · ${selectedDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="db-shell">
        <section className="db-left">
          {/* Poster */}
          <div className="db-poster">
            <div className="db-head">
              <div className="db-head-eye">FEEDBACK TODAY · <em>LIVE</em></div>
              <div className="db-stamp">UPDATED <em>{timeStr}</em> · {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}</div>
            </div>
            <div className="db-hero">{loading ? '—' : todayCount.toLocaleString('en-IN')}</div>
            <div className="db-hero-label">
              feedback collected across <em>7 areas</em>, day in progress.
              {leaderRow ? (
                <>
                  {' '}<strong>{leaderRow.label.name}{leaderRow.label.tag ? ` (${leaderRow.label.tag})` : ''}</strong> leads with {leaderRow.today} collected.
                </>
              ) : null}
            </div>

            <div className="db-secs db-secs-4">
              <div>
                <div className="eb">WINDOW · <em>{timeRange}</em></div>
                <div className="v">{windowLoading ? '—' : windowCount.toLocaleString('en-IN')}</div>
                <div className="meta">{windowLabel}</div>
              </div>
              <div>
                <div className="eb">TOTAL · ALL TIME</div>
                <div className="v">{loading ? '—' : totalCount.toLocaleString('en-IN')}</div>
                <div className="meta">SINCE FIRST RECORD</div>
              </div>
              <div>
                <div className="eb">AVG RATING · 30D</div>
                <div className="v">{avg30 != null ? avg30.toFixed(1) : '—'}<span className="unit">/5</span></div>
                <div className="meta">LAST 30 DAYS</div>
              </div>
              <div>
                <div className="eb">RESPONSE · TODAY</div>
                <div className="v">{responsePct?.pct != null ? responsePct.pct : '—'}<span className="unit">%</span></div>
                <div className="meta">{responsePct?.denom
                  ? `${responsePct.num} / ${responsePct.denom} SUPERVISORS`
                  : '—'}</div>
              </div>
            </div>
          </div>

          {/* Departure board */}
          <div className="db-board">
            <div className="db-board-head">
              <span>#</span><span>AREA</span><span>TYPE</span><span>FEEDBACK · MTD</span><span>SHARE</span><span>TODAY</span>
            </div>
            {boardRows.rows.map((row, idx) => {
              const isLeader = idx === 0 && row.today > 0;
              return (
                <div key={row.title} className={`db-board-row${isLeader ? ' leader' : ''}`}>
                  <span className="rank">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="nm">
                    {row.label.name}
                    {(isLeader || row.label.tag) ? (
                      <span className="tag">{isLeader ? 'LEADER' : ''}{isLeader && row.label.tag ? ' · ' : ''}{row.label.tag || ''}</span>
                    ) : null}
                  </span>
                  <span><span className={`db-chip ${row.chip}`}>{row.chip === 'ps' ? 'POLICE' : 'SHOPS'}</span></span>
                  <span className="count">{row.month.toLocaleString('en-IN')}</span>
                  <span className="share">
                    {boardRows.totalMonth > 0 ? (
                      <span className="db-share-bar-wrap">
                        <span className="db-share-pct">{row.share.toFixed(1)}%</span>
                        <span className="db-share-bar-track">
                          <span className="db-share-bar-fill" style={{ width: `${Math.min(row.share, 100).toFixed(1)}%` }} />
                        </span>
                      </span>
                    ) : '—'}
                  </span>
                  <span className={`delta ${row.today > 0 ? 'up' : ''}`}>{row.today > 0 ? `+${row.today}` : '0'}</span>
                </div>
              );
            })}
          </div>

        </section>

        {isAdmin && (
          <LiveFeedbackRail
            size={8}
            totalToday={todayCount}
            selectedDate={selectedDate}
          />
        )}
      </main>

      {isAdmin && (
      <section className="db-sup-section">
          <div className="db-sup-head">
            <div className="t">SUPERVISOR LIST · {selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase()}</div>
            <div className="filter">
              <label htmlFor="areaSelector">FILTER · AREA</label>
              <select
                id="areaSelector"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
              >
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button type="button" className="db-pill" onClick={handleDownloadSupervisorTable}>
                EXPORT
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {supervisorsLoading ? (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 10, padding: '2rem', color: 'var(--c-ink-3)',
              }}>
                <div className="loading-spinner loading-spinner-large" />
                <span className="loading-text">Loading supervisors…</span>
              </div>
            ) : filteredSupervisors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-ink-3)' }}>
                No supervisors found for the selected area.
              </div>
            ) : (
              <table className="db-table">
                <thead>
                  <tr>
                    <th>SUPERVISOR</th>
                    <th className="num">ID</th>
                    <th>ALLOTMENT</th>
                    <th>CHECKLIST · {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}</th>
                    <th className="num">FEEDBACKS · TODAY</th>
                    <th className="num">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSupervisors.map((supervisor, index) => {
                    const status = supervisor.checklistStatus;
                    const statusEl = status === 'Done'
                      ? <span className="status-done">DONE</span>
                      : status === 'Not Done'
                        ? <span className="status-nd">NOT DONE</span>
                        : status === 'Week Off'
                          ? <span className="status-wo">WEEK OFF</span>
                          : <div className="spinner" style={{ margin: 0, width: 14, height: 14 }} />;
                    return (
                      <tr key={index}>
                        <td style={{ maxWidth: 200 }}>
                          <button
                            type="button"
                            className="db-bare-link"
                            style={{ textTransform: 'none', letterSpacing: 0, fontFamily: "'Geist', sans-serif", fontSize: 14, fontWeight: 600, color: 'var(--c-ink)', borderBottomColor: 'var(--c-line-2)', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                            onClick={() => navigate('/dashboard/supervisor-information', { state: { supervisorId: supervisor.id } })}
                            title={supervisor.display_name || undefined}
                          >
                            <span style={{ color: supervisor.userStatus === 'inactive' ? 'var(--c-ink-4)' : 'var(--c-ink)' }}>
                              {supervisor.display_name || '—'}{supervisor.userStatus === 'inactive' ? ' · [INACTIVE]' : ''}
                            </span>
                          </button>
                        </td>
                        <td className="num">{supervisor.ixora_user_id || '—'}</td>
                        <td>{supervisorZoneName(supervisor) || supervisor.ixora_allotment || '—'}</td>
                        <td>{statusEl}</td>
                        <td className="num">
                          {feedbackValuesLoading ? <div className="spinner" style={{ margin: '0 0 0 auto', width: 14, height: 14 }} /> : supervisor.feedbacksToday}
                        </td>
                        <td className="num" style={{ fontWeight: 700 }}>
                          {feedbackValuesLoading ? <div className="spinner" style={{ margin: '0 0 0 auto', width: 14, height: 14 }} /> : supervisor.totalFeedbacks}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
